<p-dialog
  [visible]="visible()"
  (visibleChange)="onVisibleChange($event)"
  [modal]="true"
  [style]="{ width: '50rem', maxWidth: '90vw' }"
  [draggable]="false"
  [resizable]="false"
  [closable]="!isSaving()">
  <ng-template #header>
    <div class="dialog-header">
      <span>{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.TITLE' | translate }}</span>
    </div>
  </ng-template>
  <div class="custom-config-form">
    <div class="config-section two-column">
      <div class="form-field">
        <label for="custom-name">
          {{ 'SETTINGS_SMS.GATEWAY.NAME' | translate }}
          <span class="required">*</span>
        </label>
        <input
          pInputText
          id="custom-name"
          type="text"
          [ngModel]="formModel().name"
          (ngModelChange)="updateField('name', $event)"
          class="w-full"
          [class.ng-invalid]="!!serverErrors.get('name')"
          [attr.aria-invalid]="!!serverErrors.get('name') || null"
          [attr.aria-describedby]="serverErrors.get('name') ? 'custom-name-error' : null"
          required />
        @if (serverErrors.get('name')) {
          <small id="custom-name-error" class="p-error" role="alert">
            {{ serverErrors.get('name') }}
          </small>
        }
      </div>
      <div class="form-field toggle-field">
        <span class="toggle-spacer" aria-hidden="true">&nbsp;</span>
        <div class="toggle-inline">
          <p-toggleSwitch
            id="custom-enabled"
            [ngModel]="formModel().enabled"
            (ngModelChange)="updateField('enabled', $event)"
            [disabled]="isEditingSystem()" />
          <span class="toggle-label">{{ 'SETTINGS_SMS.GATEWAY.ENABLED' | translate }}</span>
        </div>
      </div>
    </div>

    <!-- Basic Configuration Section -->
    <div class="config-section">
      <h3 class="section-header">{{ 'SETTINGS_SMS.GATEWAY.BASIC_CONFIGURATION' | translate }}</h3>
      <div class="form-fields">
        <div class="form-field">
          <label for="custom-api-endpoint">
            {{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.API_ENDPOINT_URL' | translate }}
            <span class="required">*</span>
          </label>
          <input
            pInputText
            id="custom-api-endpoint"
            type="text"
            [ngModel]="formModel().url"
            (ngModelChange)="updateField('url', $event)"
            placeholder="{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.ENTER_API_ENDPOINT_URL' | translate }}"
            class="w-full"
            [class.ng-invalid]="!!serverErrors.get('url') || !!urlClientError()"
            [attr.aria-invalid]="!!serverErrors.get('url') || !!urlClientError() || null"
            [attr.aria-describedby]="serverErrors.get('url') || urlClientError() ? 'custom-api-endpoint-error' : null"
            required />
          @if (serverErrors.get('url') || urlClientError()) {
            <small id="custom-api-endpoint-error" class="p-error" role="alert">
              {{ serverErrors.get('url') ?? (urlClientError()! | translate) }}
            </small>
          }
        </div>
        <div class="form-fields-row">
          <div class="form-field">
            <label for="custom-http-method">{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.HTTP_METHOD' | translate }}</label>
            <p-select
              id="custom-http-method"
              [ngModel]="formModel().httpMethod"
              (ngModelChange)="updateField('httpMethod', $event)"
              [options]="httpMethodOptions"
              placeholder="{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.SELECT_HTTP_METHOD' | translate }}"
              class="w-full"
              appendTo="body"
              [showClear]="true"
              [disabled]="isEditingSystem()" />
          </div>
          <div class="form-field">
            <label for="custom-content-type">{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.CONTENT_TYPE' | translate }}</label>
            <p-select
              id="custom-content-type"
              [ngModel]="formModel().contentType"
              (ngModelChange)="updateField('contentType', $event)"
              [options]="contentTypeOptions"
              placeholder="{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.SELECT_CONTENT_TYPE' | translate }}"
              class="w-full"
              appendTo="body"
              [showClear]="true"
              [disabled]="isEditingSystem()" />
          </div>
        </div>
      </div>
    </div>

    <!-- Authentication Section -->
    <div class="config-section">
      <h3 class="section-header">{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.AUTHENTICATION' | translate }}</h3>
      <div class="form-fields">
        <div class="form-field">
          <label for="custom-authentication">{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.AUTHENTICATION' | translate }}</label>
          <p-select
            id="custom-authentication"
            [ngModel]="formModel().authMethod"
            (ngModelChange)="onAuthMethodChange($event)"
            [options]="authenticationOptions"
            placeholder="{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.SELECT_AUTHENTICATION_TYPE' | translate }}"
            class="w-full"
            appendTo="body"
            [showClear]="true"
            [disabled]="isEditingSystem()" />
        </div>
        @if (formModel().authMethod === 'BASIC') {
          <div class="form-field">
            <label for="custom-username">
              {{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.USERNAME' | translate }}
              <span class="required">*</span>
            </label>
            <input
              pInputText
              id="custom-username"
              type="text"
              [ngModel]="formModel().basicUsername"
              (ngModelChange)="updateField('basicUsername', $event)"
              placeholder="{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.ENTER_USERNAME' | translate }}"
              class="w-full"
              [disabled]="isEditingSystem()"
              [class.ng-invalid]="!!serverErrors.get('basicUsername')"
              [attr.aria-invalid]="!!serverErrors.get('basicUsername') || null"
              [attr.aria-describedby]="serverErrors.get('basicUsername') ? 'custom-username-error' : null"
              required />
            @if (serverErrors.get('basicUsername')) {
              <small id="custom-username-error" class="p-error" role="alert">
                {{ serverErrors.get('basicUsername') }}
              </small>
            }
          </div>
          <div class="form-field">
            <label for="custom-password">
              {{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.PASSWORD' | translate }}
              <span class="required">*</span>
            </label>
            <input
              pInputText
              id="custom-password"
              type="password"
              [ngModel]="formModel().basicPassword"
              (ngModelChange)="updateField('basicPassword', $event)"
              placeholder="{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.ENTER_PASSWORD' | translate }}"
              class="w-full"
              [disabled]="isEditingSystem()"
              [class.ng-invalid]="!!serverErrors.get('basicPassword')"
              [attr.aria-invalid]="!!serverErrors.get('basicPassword') || null"
              [attr.aria-describedby]="serverErrors.get('basicPassword') ? 'custom-password-error' : null"
              required />
            @if (serverErrors.get('basicPassword')) {
              <small id="custom-password-error" class="p-error" role="alert">
                {{ serverErrors.get('basicPassword') }}
              </small>
            }
          </div>
        }
        @if (formModel().authMethod === 'OAUTH') {
          <div class="form-field">
            <label for="custom-oauth-token-url">
              {{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.OAUTH_TOKEN_URL' | translate }}
              <span class="required">*</span>
            </label>
            <input
              pInputText
              id="custom-oauth-token-url"
              type="text"
              [ngModel]="formModel().oauthUrl"
              (ngModelChange)="updateField('oauthUrl', $event)"
              placeholder="{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.ENTER_OAUTH_TOKEN_URL' | translate }}"
              class="w-full"
              [disabled]="isEditingSystem()"
              [class.ng-invalid]="!!serverErrors.get('oauthUrl') || !!oauthUrlClientError()"
              [attr.aria-invalid]="!!serverErrors.get('oauthUrl') || !!oauthUrlClientError() || null"
              [attr.aria-describedby]="serverErrors.get('oauthUrl') || oauthUrlClientError() ? 'custom-oauth-token-url-error' : null"
              required />
            @if (serverErrors.get('oauthUrl') || oauthUrlClientError()) {
              <small id="custom-oauth-token-url-error" class="p-error" role="alert">
                {{ serverErrors.get('oauthUrl') ?? (oauthUrlClientError()! | translate) }}
              </small>
            }
          </div>
          <div class="form-fields-row">
            <div class="form-field">
              <label for="custom-client-id">
                {{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.CLIENT_ID' | translate }}
                <span class="required">*</span>
              </label>
              <input
                pInputText
                id="custom-client-id"
                type="text"
                [ngModel]="formModel().oauthClientId"
                (ngModelChange)="updateField('oauthClientId', $event)"
                placeholder="{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.ENTER_CLIENT_ID' | translate }}"
                class="w-full"
                [disabled]="isEditingSystem()"
                [class.ng-invalid]="!!serverErrors.get('oauthClientId')"
                [attr.aria-invalid]="!!serverErrors.get('oauthClientId') || null"
                [attr.aria-describedby]="serverErrors.get('oauthClientId') ? 'custom-client-id-error' : null"
                required />
              @if (serverErrors.get('oauthClientId')) {
                <small id="custom-client-id-error" class="p-error" role="alert">
                  {{ serverErrors.get('oauthClientId') }}
                </small>
              }
            </div>
            <div class="form-field">
              <label for="custom-client-secret">
                {{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.CLIENT_SECRET' | translate }}
                <span class="required">*</span>
              </label>
              <input
                pInputText
                id="custom-client-secret"
                type="password"
                [ngModel]="formModel().oauthClientSecret"
                (ngModelChange)="updateField('oauthClientSecret', $event)"
                placeholder="{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.ENTER_CLIENT_SECRET' | translate }}"
                class="w-full"
                [disabled]="isEditingSystem()"
                [class.ng-invalid]="!!serverErrors.get('oauthClientSecret')"
                [attr.aria-invalid]="!!serverErrors.get('oauthClientSecret') || null"
                [attr.aria-describedby]="serverErrors.get('oauthClientSecret') ? 'custom-client-secret-error' : null"
                required />
              @if (serverErrors.get('oauthClientSecret')) {
                <small id="custom-client-secret-error" class="p-error" role="alert">
                  {{ serverErrors.get('oauthClientSecret') }}
                </small>
              }
            </div>
          </div>
          <div class="form-fields-row">
            <div class="form-field">
              <label for="custom-scope">
                {{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.SCOPE' | translate }}
              </label>
              <input
                pInputText
                id="custom-scope"
                type="text"
                [ngModel]="formModel().oauthScope"
                (ngModelChange)="updateField('oauthScope', $event)"
                placeholder="{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.ENTER_SCOPE' | translate }}"
                class="w-full"
                [disabled]="isEditingSystem()"
                [class.ng-invalid]="!!serverErrors.get('oauthScope')"
                [attr.aria-invalid]="!!serverErrors.get('oauthScope') || null"
                [attr.aria-describedby]="serverErrors.get('oauthScope') ? 'custom-scope-error' : null" />
              @if (serverErrors.get('oauthScope')) {
                <small id="custom-scope-error" class="p-error" role="alert">
                  {{ serverErrors.get('oauthScope') }}
                </small>
              }
            </div>
            <div class="form-field">
              <label for="custom-grant-type">{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.GRANT_TYPE' | translate }}</label>
              <input
                pInputText
                id="custom-grant-type"
                type="text"
                [ngModel]="formModel().oauthGrantType"
                (ngModelChange)="updateField('oauthGrantType', $event)"
                placeholder="{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.ENTER_GRANT_TYPE' | translate }}"
                class="w-full"
                [disabled]="isEditingSystem()"
                [class.ng-invalid]="!!serverErrors.get('oauthGrantType')"
                [attr.aria-invalid]="!!serverErrors.get('oauthGrantType') || null"
                [attr.aria-describedby]="serverErrors.get('oauthGrantType') ? 'custom-grant-type-error' : null" />
              @if (serverErrors.get('oauthGrantType')) {
                <small id="custom-grant-type-error" class="p-error" role="alert">
                  {{ serverErrors.get('oauthGrantType') }}
                </small>
              }
            </div>
          </div>
        }
        @if (formModel().authMethod === 'API_KEY_HEADER') {
          <div class="form-field">
            <label for="custom-api-key-name">
              {{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.HEADER_NAME' | translate }}
              <span class="required">*</span>
            </label>
            <input
              pInputText
              id="custom-api-key-name"
              type="text"
              [ngModel]="formModel().apiKeyHeaderName"
              (ngModelChange)="updateField('apiKeyHeaderName', $event)"
              placeholder="{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.ENTER_HEADER_NAME' | translate }}"
              class="w-full"
              [disabled]="isEditingSystem()"
              [class.ng-invalid]="!!serverErrors.get('apiKeyHeaderName')"
              [attr.aria-invalid]="!!serverErrors.get('apiKeyHeaderName') || null"
              [attr.aria-describedby]="serverErrors.get('apiKeyHeaderName') ? 'custom-api-key-name-error' : null"
              required />
            @if (serverErrors.get('apiKeyHeaderName')) {
              <small id="custom-api-key-name-error" class="p-error" role="alert">
                {{ serverErrors.get('apiKeyHeaderName') }}
              </small>
            }
          </div>
          <div class="form-field">
            <label for="custom-api-key-value">
              {{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.HEADER_VALUE' | translate }}
              <span class="required">*</span>
            </label>
            <input
              pInputText
              id="custom-api-key-value"
              type="text"
              [ngModel]="formModel().apiKeyHeaderValue"
              (ngModelChange)="updateField('apiKeyHeaderValue', $event)"
              placeholder="{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.ENTER_HEADER_VALUE' | translate }}"
              class="w-full"
              [disabled]="isEditingSystem()"
              [class.ng-invalid]="!!serverErrors.get('apiKeyHeaderValue')"
              [attr.aria-invalid]="!!serverErrors.get('apiKeyHeaderValue') || null"
              [attr.aria-describedby]="serverErrors.get('apiKeyHeaderValue') ? 'custom-api-key-value-error' : null"
              required />
            @if (serverErrors.get('apiKeyHeaderValue')) {
              <small id="custom-api-key-value-error" class="p-error" role="alert">
                {{ serverErrors.get('apiKeyHeaderValue') }}
              </small>
            }
          </div>
        }
      </div>
    </div>

    <!-- Parameters Section -->
    <div class="config-section">
      <div class="section-header-row">
        <h3 class="section-header">{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.PARAMETERS' | translate }}</h3>
        <p-button
          [label]="'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.ADD_PARAMETER' | translate"
          icon="pi pi-plus"
          severity="secondary"
          [outlined]="true"
          [disabled]="isEditingSystem()"
          (onClick)="addParameter()" />
      </div>
      <div class="section-divider"></div>
      <div class="form-fields">
        @if (parameters().length === 0) {
          <p class="no-parameters">{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.NO_PARAMETERS_ADDED' | translate }}</p>
        } @else {
          @for (parameter of parameters(); track parameter.id; let i = $index) {
            <div class="parameter-item">
              <div class="parameter-header">
                <span class="parameter-title">{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.PARAMETER' | translate }} {{ i + 1 }}</span>
                <p-button
                  icon="pi pi-trash"
                  severity="danger"
                  [text]="true"
                  [rounded]="true"
                  [disabled]="isEditingSystem()"
                  (onClick)="removeParameter(parameter.id)"
                  [pTooltip]="'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.REMOVE_PARAMETER' | translate"
                  tooltipPosition="top" />
              </div>
              <div class="parameter-content">
                <div class="form-field">
                  <label for="parameter-key-{{ parameter.id }}">
                    {{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.PARAMETER_KEY' | translate }}
                    <span class="required">*</span>
                  </label>
                  <input
                    pInputText
                    id="parameter-key-{{ parameter.id }}"
                    type="text"
                    [ngModel]="parameter.key"
                    (ngModelChange)="onParameterFieldChange(parameter.id, 'key', $event)"
                    placeholder="{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.ENTER_PARAMETER_KEY' | translate }}"
                    class="w-full"
                    [disabled]="isEditingSystem()"
                    [class.ng-invalid]="!!paramError(parameter.id, 'key')"
                    [attr.aria-invalid]="!!paramError(parameter.id, 'key') || null"
                    [attr.aria-describedby]="paramError(parameter.id, 'key') ? 'parameter-key-' + parameter.id + '-error' : null"
                    required />
                  @if (paramError(parameter.id, 'key')) {
                    <small id="parameter-key-{{ parameter.id }}-error" class="p-error" role="alert">
                      {{ paramError(parameter.id, 'key') }}
                    </small>
                  }
                </div>
                <div class="parameter-dropdowns-row">
                  <div class="form-field">
                    <label for="parameter-type-{{ parameter.id }}">{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.PARAMETER_TYPE' | translate }}</label>
                    <p-select
                      id="parameter-type-{{ parameter.id }}"
                      [ngModel]="parameter.type"
                      (ngModelChange)="onParameterFieldChange(parameter.id, 'type', $event)"
                      [options]="parameterTypeOptions"
                      placeholder="{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.SELECT_PARAMETER_TYPE' | translate }}"
                      class="w-full"
                      appendTo="body"
                      [showClear]="true"
                      [disabled]="isEditingSystem()"
                      [class.ng-invalid]="!!paramError(parameter.id, 'type')"
                      [attr.aria-invalid]="!!paramError(parameter.id, 'type') || null"
                      [attr.aria-describedby]="paramError(parameter.id, 'type') ? 'parameter-type-' + parameter.id + '-error' : null" />
                    @if (paramError(parameter.id, 'type')) {
                      <small id="parameter-type-{{ parameter.id }}-error" class="p-error" role="alert">
                        {{ paramError(parameter.id, 'type') }}
                      </small>
                    }
                  </div>
                  <div class="form-field">
                    <label for="parameter-location-{{ parameter.id }}">{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.LOCATION' | translate }}</label>
                    <p-select
                      id="parameter-location-{{ parameter.id }}"
                      [ngModel]="parameter.place"
                      (ngModelChange)="onParameterFieldChange(parameter.id, 'place', $event)"
                      [options]="parameterLocationOptions"
                      placeholder="{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.SELECT_LOCATION' | translate }}"
                      class="w-full"
                      appendTo="body"
                      [showClear]="true"
                      [disabled]="isEditingSystem()"
                      [class.ng-invalid]="!!paramError(parameter.id, 'place')"
                      [attr.aria-invalid]="!!paramError(parameter.id, 'place') || null"
                      [attr.aria-describedby]="paramError(parameter.id, 'place') ? 'parameter-location-' + parameter.id + '-error' : null" />
                    @if (paramError(parameter.id, 'place')) {
                      <small id="parameter-location-{{ parameter.id }}-error" class="p-error" role="alert">
                        {{ paramError(parameter.id, 'place') }}
                      </small>
                    }
                  </div>
                </div>
                @if (parameter.type === 'SENDER_NUMBER' || parameter.type === 'EXTRA' || parameter.type === 'AUTH') {
                  <div class="form-field">
                    <label for="parameter-static-value-{{ parameter.id }}">
                      {{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.STATIC_VALUE' | translate }}
                    </label>
                    <input
                      pInputText
                      id="parameter-static-value-{{ parameter.id }}"
                      type="text"
                      [ngModel]="parameter.staticValue"
                      (ngModelChange)="onParameterFieldChange(parameter.id, 'staticValue', $event)"
                      placeholder="{{ 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.ENTER_STATIC_VALUE' | translate }}"
                      class="w-full"
                      [disabled]="isEditingSystem()"
                      [class.ng-invalid]="!!paramError(parameter.id, 'staticValue')"
                      [attr.aria-invalid]="!!paramError(parameter.id, 'staticValue') || null"
                      [attr.aria-describedby]="paramError(parameter.id, 'staticValue') ? 'parameter-static-value-' + parameter.id + '-error' : null" />
                    @if (paramError(parameter.id, 'staticValue')) {
                      <small id="parameter-static-value-{{ parameter.id }}-error" class="p-error" role="alert">
                        {{ paramError(parameter.id, 'staticValue') }}
                      </small>
                    }
                  </div>
                }
              </div>
            </div>
          }
        }
      </div>
    </div>
  </div>

  <ng-template #footer>
    <p-button
      [label]="'GENERAL.CANCEL' | translate"
      severity="secondary"
      [outlined]="true"
      [disabled]="isSaving()"
      (onClick)="onCancel()" />
    <p-button
      [label]="'GENERAL.SAVE' | translate"
      severity="primary"
      [loading]="isSaving()"
      [disabled]="isSaving() || !isValid()"
      (onClick)="handleSave()" />
  </ng-template>
</p-dialog>
